// Caches the staff.users registry as a lookup map keyed by id.
//
// Single Refine `useList` against the `staff` schema's `users` table. Refine
// uses React Query under the hood, so we lean on its cache: 5-minute stale
// window means tens of "Last edited by" cells in a list view share one fetch
// instead of one per row. RLS on staff.users grants SELECT to authenticated,
// so the publishable-key client can read this safely.
//
// Returns a stable `userMap` reference between renders (memoised) so consumers
// don't re-render when unrelated query state changes.

import { useMemo } from "react";
import { useList } from "@refinedev/core";

export interface StaffUser {
  id: string;
  display_name: string | null;
  short_name: string | null;
  email: string | null;
}

export type UserMap = Record<string, StaffUser>;

const FIVE_MINUTES = 1000 * 60 * 5;

export function useUserMap(): {
  userMap: UserMap;
  isLoading: boolean;
  error: unknown;
} {
  // Refine v5 return shape:
  //   - `result.data`        normalized rows (was `data.data` in v4)
  //   - `query.isLoading`    TanStack Query state (was top-level in v4)
  //   - `query.error`        TanStack Query error  (was top-level in v4)
  const { result, query } = useList<StaffUser>({
    resource: "users",
    // staff.users — Refine's Supabase data provider routes via PostgREST,
    // and we point it at the `staff` schema explicitly.
    meta: { schema: "staff" },
    pagination: { mode: "off" },
    queryOptions: {
      staleTime: FIVE_MINUTES,
      // Don't refetch on window focus for a directory that changes weekly
      // at most. Refine wires this through to React Query.
      refetchOnWindowFocus: false,
    },
  });

  const rows = result?.data;
  const userMap = useMemo<UserMap>(() => {
    const map: UserMap = {};
    for (const row of rows ?? []) {
      if (row?.id) map[row.id] = row;
    }
    return map;
  }, [rows]);

  return { userMap, isLoading: query.isLoading, error: query.error };
}
